import { globalHandlersIntegration, type BrowserOptions, type ErrorEvent } from "@sentry/react";

type TransactionEvent = Parameters<NonNullable<BrowserOptions["beforeSendTransaction"]>>[0];

export interface PrivatePageTelemetry {
  errorMessage: string;
  events: readonly string[];
  timings: readonly { name: string; op: string }[];
}

export const privatePageSentryOptions = (policy: PrivatePageTelemetry) => ({
  defaultIntegrations: false,
  integrations: [globalHandlersIntegration()],
  sendDefaultPii: false,
  tracePropagationTargets: [],
  beforeSend: (event: ErrorEvent) => sanitizePrivateEvent(event, policy),
  beforeSendTransaction: (event: TransactionEvent) => sanitizePrivateTransaction(event, policy),
} satisfies BrowserOptions);

export const sanitizePrivateEvent = (event: ErrorEvent, policy: PrivatePageTelemetry): ErrorEvent => {
  if (!event.exception && event.level === "info" &&
    event.message && policy.events.includes(event.message)) {
    return {
      type: undefined,
      event_id: event.event_id,
      timestamp: event.timestamp,
      level: "info",
      message: event.message,
    };
  }
  return sanitizePrivateError(event, policy.errorMessage);
};

// Only declared timings leave this document. Drop automatic span
// payloads and SDK sampling metadata too: both can carry URLs or scope data.
const sanitizePrivateTransaction = (event: TransactionEvent, policy: PrivatePageTelemetry): TransactionEvent | null => {
  const operation = policy.timings.find((timing) => timing.name === event.transaction)?.op;
  const trace = event.contexts?.trace;
  if (!operation || !trace) return null;
  return {
    type: "transaction",
    event_id: event.event_id,
    transaction: event.transaction,
    start_timestamp: event.start_timestamp,
    timestamp: event.timestamp,
    contexts: {
      trace: {
        trace_id: trace.trace_id,
        span_id: trace.span_id,
        op: operation,
        status: trace.status === "internal_error" ? "internal_error" : "ok",
      },
    },
    spans: [],
  };
};

// Allowlist diagnostic fields: tokens can also appear in messages, breadcrumbs,
// request bodies, and arbitrary context, not only the page URL.
const sanitizePrivateError = (event: ErrorEvent, errorMessage: string): ErrorEvent => ({
  type: undefined,
  event_id: event.event_id,
  timestamp: event.timestamp,
  level: "error",
  exception: {
    values: [{
      type: "Error",
      value: errorMessage,
      stacktrace: {
        frames: event.exception?.values?.flatMap((exception) =>
          exception.stacktrace?.frames?.map((frame) => ({
            filename: frame.filename?.split(/[?#]/)[0],
            lineno: frame.lineno,
            colno: frame.colno,
          })) ?? [],
        ),
      },
    }],
  },
});
